import { useState } from "react";
import { ChevronDown, ChevronRight, Fuel, Calendar, Ship, FileText } from "lucide-react";

const MEFuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const fuelTypes = [
    {
      id: "hfo",
      title: "Heavy Fuel Oil (HFO)",
      description: "Primary fuel for main propulsion",
      calculation: "Sum of all mainEngineConsMTHFO field values for each date",
      output: "Daily total HFO consumption in metric tons",
      field: "mainEngineConsMTHFO",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "lfo",
      title: "Light Fuel Oil (LFO)",
      description: "Used for various engine operations",
      calculation: "Sum of all mainEngineConsMTLFO field values for each date",
      output: "Daily total LFO consumption in metric tons",
      field: "mainEngineConsMTLFO",
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "mgo",
      title: "Marine Gas Oil (MGO)",
      description: "Clean fuel for auxiliary systems",
      calculation: "Sum of all mainEngineConsMTMGO field values for each date",
      output: "Daily total MGO consumption in metric tons",
      field: "mainEngineConsMTMGO",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "others",
      title: "Other Fuel Types",
      description: "Sum of Ethane and Biofuel consumption",
      calculation: "Sum of mainEngineConsMTEthane + mainEngineConsMTBiofuel for each date",
      output: "Daily total other fuel consumption in metric tons",
      field: "mainEngineConsMTEthane + mainEngineConsMTBiofuel",
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#d5e7f7] rounded-lg">
              <Fuel className="w-6 h-6 text-[#566573]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Main Engine Fuel Consumption
              </h2>
              <p className="text-gray-600 text-sm">
                Daily fuel consumption aggregation from VesselPerformance records
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Fuel Type Calculations */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Fuel Type Calculations</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {fuelTypes.map((fuel) => (
                  <div
                    key={fuel.id}
                    className={`p-5 rounded-lg border ${fuel.borderColor} ${fuel.bgColor}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold text-gray-800">
                        {fuel.title}
                      </h4>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${fuel.textColor} ${fuel.bgColor.replace('50', '100')}`}>
                        Field: {fuel.field}
                      </span>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                      <p className="text-sm text-gray-600">{fuel.description}</p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Calculation Logic:</p>
                      <div className="text-sm text-gray-600 bg-white p-2 rounded border">
                        {fuel.calculation}
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Aggregation Method:</p>
                      <p className="text-sm text-gray-600">
                        Daily sum grouped by <code className="text-xs bg-gray-100 px-1 rounded">recordedAt</code> date
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Output Format:</p>
                      <p className={`text-sm font-semibold ${fuel.textColor}`}>
                        {fuel.output}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MEFuelConsumption;